import { Product } from "@/hooks/useInventory";

interface InventoryHook {
  products: Product[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  fetchInventory: () => void;
}

interface InventorySectionProps {
  activeRValue?: string | null;
  onClearFilter?: () => void;
  inventory: InventoryHook;
}

export default function InventorySection({ activeRValue, onClearFilter, inventory }: InventorySectionProps) {
  const { products, loading, error, lastUpdated, fetchInventory } = inventory;

  const extractRNumber = (val: string) => val.replace(/[^0-9]/g, "");

  const filteredProducts = activeRValue
    ? products.filter((p) => {
        const num = extractRNumber(activeRValue);
        return (
          (p.name && extractRNumber(p.name) === num && p.name.toUpperCase().includes("R")) ||
          (p.rValue && extractRNumber(p.rValue) === num && p.rValue.toUpperCase().includes("R"))
        );
      })
    : products;

  return (
    <section id="inventory" className="py-20 px-4 bg-[#f5f5f0]">
      <div className="max-w-7xl mx-auto">

        {/* Section label */}
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="h-px w-10 bg-red-600 inline-block"></span>
          <span className="text-red-600 text-xs font-black uppercase tracking-[0.2em]">Real-Time Stock</span>
          <span className="h-px w-10 bg-red-600 inline-block"></span>
        </div>

        {/* Heading */}
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 text-center tracking-tight mb-3">
          Live Inventory
        </h2>
        

        {/* Active filter banner */}
        {activeRValue && (
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-3 bg-red-600 text-white text-sm font-black px-5 py-2.5 rounded-full">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-filter-3-line text-sm"></i>
              </div>
              Showing: {activeRValue} only
              <button
                onClick={onClearFilter}
                className="ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-colors cursor-pointer"
                aria-label="Clear filter"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          </div>
        )}

        {/* Info pills */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
          <span className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-xs font-bold px-4 py-2 rounded-full whitespace-nowrap">
            <i className="ri-map-pin-2-line text-red-500 text-sm"></i>
            Buford, GA 30518
          </span>
          <span className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-xs font-bold px-4 py-2 rounded-full whitespace-nowrap">
            <i className="ri-calendar-check-line text-red-500 text-sm"></i>
            Pickup by Appointment
          </span>
          <a
            href="tel:678-936-5391"
            className="flex items-center gap-2 bg-red-600 text-white text-xs font-black px-4 py-2 rounded-full hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line text-sm"></i>
            678-936-5391
          </a>
        </div>

        {/* Main card */}
        <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white">

          {/* Card top bar */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100">
                  <i className="ri-table-line text-red-600 text-base"></i>
                </div>
                <span className="text-gray-800 text-sm font-black">Insulation Inventory</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && !loading && (
                <span className="hidden sm:flex items-center gap-1.5 text-xs text-green-600 font-bold">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block animate-pulse"></span>
                  {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              <button
                onClick={fetchInventory}
                disabled={loading}
                className="flex items-center gap-1.5 bg-gray-900 hover:bg-gray-700 text-white text-xs font-black px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`}></i>
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>

          {/* States */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 border border-red-100">
                <i className="ri-refresh-line animate-spin text-2xl text-red-500"></i>
              </div>
              <span className="text-gray-400 text-sm font-semibold">Loading live inventory...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 border border-red-100">
                <i className="ri-wifi-off-line text-2xl text-red-500"></i>
              </div>
              <p className="text-gray-500 text-sm font-semibold">{error}</p>
              <button onClick={fetchInventory} className="text-xs text-red-600 font-bold underline cursor-pointer hover:text-red-700">
                Try again
              </button>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 border border-gray-200">
                <i className="ri-inbox-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 text-sm font-semibold">
                {activeRValue ? `No ${activeRValue} items in stock right now.` : "No in-stock items right now. Check back soon!"}
              </p>
              {activeRValue && (
                <button onClick={onClearFilter} className="text-xs text-red-600 font-bold underline cursor-pointer hover:text-red-700">
                  Show all inventory
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table — hidden on mobile */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-gray-400">Product</th>
                      <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-gray-400">Width</th>
                      <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-gray-400">Faced / Unfaced</th>
                      <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-gray-400">Sq Ft Per Bag</th>
                      <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-gray-400">Price</th>
                      <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-gray-400">Quantity Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product, idx) => (
                      <tr
                        key={product.id}
                        className={`border-b border-gray-50 transition-colors hover:bg-red-50/40 ${
                          idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                        }`}
                      >
                        <td className="px-6 py-4 font-black text-gray-900 text-sm whitespace-nowrap">
                          {product.name || "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {product.rValue ? (
                            <span className="bg-red-600 text-white text-xs font-black px-3 py-1 rounded-full">
                              {product.rValue}
                            </span>
                          ) : (
                            <span className="text-gray-300 font-bold">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-600 font-bold text-sm whitespace-nowrap">{product.size || "—"}</td>
                        <td className="px-6 py-4 text-gray-600 font-bold text-sm whitespace-nowrap">{product.coverage || "—"}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-green-600 font-black text-base">{product.price || "—"}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block flex-shrink-0"></span>
                            <span className="text-gray-900 font-black text-base">{product.qty}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards — shown only on mobile */}
              <div className="md:hidden divide-y divide-gray-100">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="px-4 py-5">
                    {/* Product name + Width badge */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="font-black text-gray-900 text-base leading-snug">{product.name || "—"}</p>
                      {product.rValue && (
                        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                          <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wide">Width</span>
                          <span className="bg-red-600 text-white text-xs font-black px-3 py-1 rounded-full whitespace-nowrap">
                            {product.rValue} inches
                          </span>
                        </div>
                      )}
                    </div>
                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
                        <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wide mb-0.5">Faced/Unfaced</p>
                        <p className="text-gray-800 font-black text-sm">{product.size || "—"}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
                        <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wide mb-0.5">Sq Ft / Bag</p>
                        <p className="text-gray-800 font-black text-sm">{product.coverage || "—"}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
                        <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wide mb-0.5">Qty Available</p>
                        <p className="text-green-600 font-black text-sm flex items-center justify-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                          {product.qty}
                        </p>
                      </div>
                    </div>
                    {/* Price */}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-gray-400 text-xs font-bold uppercase tracking-wide">Price</span>
                      <span className="text-green-600 font-black text-xl">{product.price || "—"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Card bottom bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
            <p className="text-gray-400 text-xs font-semibold flex items-center gap-1.5">
              <i className="ri-information-line text-sm"></i>
              Prices subject to change. Confirm availability before pickup.
            </p>
            <a
              href="tel:678-936-5391"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs font-black px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Call or Text 678-936-5391
            </a>
          </div>
        </div>

        {/* Bottom stat strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {[
            { icon: "ri-price-tag-3-line", value: "Lowest Prices", sub: "Guaranteed cheapest in town" },
            { icon: "ri-shield-check-line", value: "Brand New Stock", sub: "Never used or reclaimed" },
            { icon: "ri-truck-line", value: "Fresh Truckloads", sub: "New inventory arrives regularly" },
          ].map((stat) => (
            <div
              key={stat.value}
              className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-50 border border-red-100 flex-shrink-0">
                <i className={`${stat.icon} text-red-600 text-lg`}></i>
              </div>
              <div>
                <p className="text-gray-900 font-black text-sm">{stat.value}</p>
                <p className="text-gray-400 text-xs font-medium">{stat.sub}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
